/**
 * Usage Tracking Service
 * Handles fetching usage limits and current counts from the backend API
 */

// import { AUTH_TOKEN } from 'constants/AuthConstant';
import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { updatePlanLimitsWithUsage } from 'configs/LimitsConfig';
import API from './API';

/**
 * Fetch usage limits and current counts from the backend API
 * @returns {Promise<Object>} Usage data with limits and counts
 */
export const fetchUsageLimitsAndCounts = async () => {
  try {
    // const token = localStorage.getItem(AUTH_TOKEN);
    const token = localStorage.getItem(SEC_AUTH_M_TK);

    if (!token) {
      console.warn('No authentication token available for usage tracking');
      return null;
    }

    const response = await API.get('/max_limit_and_count');
    const data = response.data;
    
    console.log('📊 Usage tracking data received:', data);

    // Validate that we have the expected structure
    if (typeof data === 'object' && data !== null) {
      return data;
    } else {
      console.warn('Invalid usage tracking data structure received');
      return null;
    }

  } catch (error) {
    console.error('Error fetching usage limits and counts:', error);
    return null;
  }
};

/**
 * Transform API response to match internal limit structure
 * @param {Object} apiData - Raw API response data
 * @returns {Object} Transformed data with limits and usage counts
 */
export const transformUsageData = (apiData) => {
  if (!apiData || typeof apiData !== 'object') {
    return null;
  }

  // Extract limits (max_* properties)
  const limits = {};
  const usageCounts = {};

  Object.keys(apiData).forEach(key => {
    const value = apiData[key];
    
    // Check if property exists and is a valid number
    if (typeof value === 'number' && !isNaN(value)) {
      if (key.startsWith('max_')) {
        // Convert API naming to internal naming convention
        const limitKey = key.replace('max_', '').toUpperCase();
        limits[limitKey] = value;
      } else if (key.endsWith('_count')) {
        // Store usage counts
        usageCounts[key] = value;
      }
    }
  });

  console.log('📊 Transformed usage data:', { limits, usageCounts });

  return {
    limits,
    usageCounts,
    rawData: apiData
  };
};

/**
 * Update the limits configuration with API data
 * @param {Object} apiData - Raw API response data
 * @param {string} subscriptionType - User's subscription type
 */
export const updateLimitsWithApiData = (apiData, subscriptionType) => {
  try {
    const transformedData = transformUsageData(apiData);
    
    if (!transformedData) {
      console.warn('No valid usage data to update limits with');
      return false;
    }

    // Update the limits configuration
    updatePlanLimitsWithUsage(subscriptionType, transformedData.limits, transformedData.usageCounts);
    
    console.log('✅ Limits updated with API data for subscription:', subscriptionType);
    return true;

  } catch (error) {
    console.error('Error updating limits with API data:', error);
    return false;
  }
};

/**
 * Safe property access with fallback
 * @param {Object} obj - Object to access property from
 * @param {string} property - Property name to access
 * @param {*} fallback - Fallback value if property doesn't exist
 * @returns {*} Property value or fallback
 */
export const safePropertyAccess = (obj, property, fallback = 0) => {
  if (!obj || typeof obj !== 'object') {
    return fallback;
  }
  
  return obj.hasOwnProperty(property) && typeof obj[property] === 'number' 
    ? obj[property] 
    : fallback;
};

/**
 * Get usage count for a specific metric
 * @param {Object} usageData - Usage data object
 * @param {string} metric - Metric name (e.g., 'agent_count', 'document_count')
 * @returns {number} Usage count or 0 if not available
 */
export const getUsageCount = (usageData, metric) => {
  return safePropertyAccess(usageData, metric, 0);
};

/**
 * Get limit value for a specific metric
 * @param {Object} limitsData - Limits data object
 * @param {string} metric - Metric name (e.g., 'max_agent_creation_limit')
 * @returns {number} Limit value or fallback from static config
 */
export const getLimitValue = (limitsData, metric) => {
  return safePropertyAccess(limitsData, metric, null);
};

const logData = (data, message) => {
  console.log(message, data);
};
